import { set } from "date-fns";
import "./GridDropdown.css";
import { useEffect, useState } from "react";
import { NavDropdown } from "react-bootstrap";

export interface Props<T> {
  label: string;
  keyProvider: (item: T) => string;
  labelProvider: (item: T) => string;
  dataProvider: (text?: string) => Promise<T[]>;
  initialKeys?: string[];
  onChange: (selection: T[]) => void;
  showSearch?: boolean;
  selectionType?: "single" | "multiple"
}

export const GridDropdown = <T,>({
  label,
  keyProvider,
  labelProvider,
  dataProvider,
  initialKeys,
  onChange,
  showSearch = false,
  selectionType = "multiple"
}: Props<T>) => {
  const [loading, setLoading] = useState<boolean>();

  const [data, setData] = useState<T[]>([]);
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());

  const [searchText, setSearchText] = useState<string>();

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await dataProvider(searchText);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialKeys) {
      setSelectedValues(new Set(initialKeys));
    }
  }, [initialKeys]);

  useEffect(() => {
    loadData();
  }, [searchText]);

  const onValueChange = (item: T) => {
    const selection = new Set(selectedValues);
    const itemKey = keyProvider(item);
    if (selection.has(itemKey)) {
      selection.delete(itemKey);
    } else {
      if (selectionType === "single") {
        selection.clear();
      }
      selection.add(itemKey);
    }
    setSelectedValues(selection);
    onChange(data.filter((v) => selection.has(keyProvider(v))));
  };

  return (
    <>
      <NavDropdown
        id={"lookup-dropdown-" + label}
        title={label}
        align="end"
        className={`btn-dropdown ms-md-2 me-2 me-md-0 mt-2 mt-md-0 ${selectedValues?.size > 0 ? 'border border-primary rounded-2' : 'border border-white'}`}
      >
        <div>
          {
            <div className={`filter-container py-1`}>
              <div className={showSearch ? "d-block" : "d-none"}>
                <div className="px-2 py-1">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search"
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                </div>
              </div>
              {loading && <p className="pt-3 text-center">Loading...</p>}
              {!loading && (
                <div>
                  {selectionType === "multiple" && <div key={-1}>
                    <label className="collapse-item d-flex px-3 py-1">
                      <input
                        className="form-check-input me-2"
                        type={selectionType === "multiple" ? "checkbox" : "radio"}
                        checked={selectedValues.size === 0}
                        onChange={() => {
                          selectedValues.clear();
                          setSelectedValues(new Set(selectedValues));
                          onChange([])
                        }}
                      />
                      <div className="fs-13">All</div>
                    </label>
                  </div>}
                  {data && data.map((item: T, index: number) => (
                    <div key={index}>
                      <label className="collapse-item d-flex px-3 py-1">
                        <input
                          className="form-check-input me-2"
                          type={selectionType === "multiple" ? "checkbox" : "radio"}
                          checked={selectedValues.has(keyProvider(item))}
                          onChange={() => onValueChange(item)}
                        />
                        <div className="fs-13 text-nowrap">{labelProvider(item)}</div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          }
        </div>
      </NavDropdown>
    </>
  );
};
